/**
 * <recently-viewed-latori> - "Recently viewed products" row on the PDP
 * (sections/recently-viewed-latori.liquid).
 *
 * On connect it records the current product handle in localStorage, then
 * fetches one pre-rendered product card per previously viewed handle from the
 * Section Rendering API (sections/recently-viewed-card-latori.liquid). The
 * cards go into either the carousel <template> (same swiper-carousel-latori
 * markup as product-template-product-list.liquid) or the plain product-grid
 * <template>, using the same rule as that section: carousel only when the
 * count exceeds the smallest per-row breakpoint value. With no previously
 * viewed products the section shell simply stays hidden.
 *
 * Each stored entry is {handle, variant}: the variant id starts from the
 * page's ?variant= URL param and is updated on every "on:variant:change"
 * event from the main product's variant picker, so a revisit shows the card
 * with the variant the visitor last looked at (image + ?variant= link)
 * instead of the product's featured image. Legacy plain-string entries are
 * normalized to {handle} on read.
 *
 * Handles whose fetch returns a non-OK response (deleted/unpublished
 * products) are pruned from localStorage.
 *
 * If data-source is "recommendations", the row shows Shopify product
 * recommendations. The component uses a maximum of three seed products: the
 * current product, then the products viewed last, then the cart products.
 * It sends each seed to the recommendations API (intent=related). It takes
 * the results from each list in turn, and it removes the seeds and the cart
 * products. The cards come from the same card section as the other rows.
 *
 * A guard wraps the whole file, as in swatch-carousel-latori.js. The section
 * loads this script itself. templates/product.json renders the section twice
 * (recommendations and recently viewed). The second copy of this script must
 * be a no-op. An unguarded top-level class declaration throws a redeclaration
 * SyntaxError.
 */
if (!customElements.get("recently-viewed-latori")) {
  class RecentlyViewedLatori extends HTMLElement {
    static STORAGE_KEY = "recently-viewed-latori";
    static STORAGE_LIMIT = 24;
    static SEED_LIMIT = 3;
    static LOOKUP_LIMIT = 4;

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.currentHandle = this.dataset.productHandle;
      this.currentId = this.dataset.productId || null;
      this.maxProducts = parseInt(this.dataset.maxProducts, 10) || 8;
      const stored = this.readStorage();
      const viewedEntries = stored.filter(
        (entry) => entry.handle !== this.currentHandle,
      );

      if (this.currentHandle) {
        const currentVariant = new URLSearchParams(window.location.search).get(
          "variant",
        );
        this.writeStorage(
          [
            {
              handle: this.currentHandle,
              variant: currentVariant || null,
              id: this.currentId,
            },
            ...viewedEntries,
          ].slice(0, RecentlyViewedLatori.STORAGE_LIMIT),
        );

        this.onVariantChange = this.onVariantChange.bind(this);
        document.addEventListener("on:variant:change", this.onVariantChange);
      }

      const run =
        this.dataset.source === "recommendations"
          ? () => this.initRecommendations(viewedEntries)
          : () => this.init(viewedEntries.slice(0, this.maxProducts));
      if (this.dataset.source !== "recommendations" && viewedEntries.length === 0) {
        return;
      }

      // Defer the card fetches so they don't compete with the PDP's own
      // above-the-fold work.
      if ("requestIdleCallback" in window) {
        requestIdleCallback(run, { timeout: 2000 });
      } else {
        setTimeout(run, 200);
      }
    }

    disconnectedCallback() {
      if (this.onVariantChange) {
        document.removeEventListener("on:variant:change", this.onVariantChange);
      }
    }

    /**
     * Keeps the current product's stored entry pointing at the variant the
     * visitor selected last. Events from within this section's own injected
     * cards are ignored (they carry no variant picker, but stay safe anyway).
     */
    onVariantChange(evt) {
      const variant = evt.detail && evt.detail.variant;
      if (!variant || evt.target.closest("recently-viewed-latori")) return;

      this.writeStorage(
        this.readStorage().map((entry) =>
          entry.handle === this.currentHandle
            ? { ...entry, variant: String(variant.id) }
            : entry,
        ),
      );
    }

    readStorage() {
      try {
        const raw = localStorage.getItem(RecentlyViewedLatori.STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return [];
        return parsed
          .map((entry) => {
            if (typeof entry === "string") return { handle: entry, variant: null };
            if (entry && typeof entry.handle === "string") {
              return {
                handle: entry.handle,
                variant: typeof entry.variant === "string" ? entry.variant : null,
                id: entry.id ? String(entry.id) : null,
              };
            }
            return null;
          })
          .filter(Boolean);
      } catch (e) {
        return [];
      }
    }

    writeStorage(entries) {
      try {
        localStorage.setItem(
          RecentlyViewedLatori.STORAGE_KEY,
          JSON.stringify(entries),
        );
      } catch (e) {
        // Storage unavailable (private mode/quota) - tracking is best-effort.
      }
    }

    async init(entries) {
      this.render(await this.fetchCards(entries));
    }

    /**
     * Collects a maximum of SEED_LIMIT seeds in this sequence: the current
     * product, the products viewed last, the cart products. Older stored
     * entries have no id. For these entries, the id comes from
     * /products/<handle>.js, a maximum of LOOKUP_LIMIT times for each page
     * view. The component saves the ids that it finds and removes the
     * entries that return 404. Thus the next page view does not repeat these
     * requests.
     */
    async initRecommendations(viewedEntries) {
      const urlRoot = (this.dataset.urlRoot || "/").replace(/\/$/, "");
      const limit = RecentlyViewedLatori.SEED_LIMIT;
      const seeds = [];
      const addSeed = (id) => {
        if (id && seeds.length < limit && !seeds.includes(String(id))) {
          seeds.push(String(id));
        }
      };

      addSeed(this.currentId);
      const foundIds = {};
      const staleHandles = [];
      let lookups = 0;
      for (const entry of viewedEntries) {
        if (seeds.length >= limit) break;
        if (entry.id) {
          addSeed(entry.id);
          continue;
        }
        if (lookups >= RecentlyViewedLatori.LOOKUP_LIMIT) continue;
        lookups += 1;
        const result = await this.fetchProductId(entry.handle);
        if (result.id) {
          foundIds[entry.handle] = result.id;
          addSeed(result.id);
        } else if (result.notFound) {
          staleHandles.push(entry.handle);
        }
      }
      if (Object.keys(foundIds).length || staleHandles.length) {
        this.writeStorage(
          this.readStorage()
            .filter((entry) => !staleHandles.includes(entry.handle))
            .map((entry) =>
              !entry.id && foundIds[entry.handle]
                ? { ...entry, id: foundIds[entry.handle] }
                : entry,
            ),
        );
      }

      let cartHandles = [];
      try {
        const cart = await (await fetch(`${urlRoot}/cart.js`)).json();
        cartHandles = (cart.items || []).map((item) => item.handle);
        (cart.items || []).forEach((item) => addSeed(item.product_id));
      } catch (e) {
        // The cart is not available. The other seeds are sufficient.
      }
      if (seeds.length === 0) return;

      const lists = await Promise.all(
        seeds.map(async (id) => {
          try {
            const url = `${this.dataset.recommendationsUrl}.json?product_id=${encodeURIComponent(id)}&limit=${this.maxProducts}&intent=related`;
            const response = await fetch(url);
            if (!response.ok) return [];
            const data = await response.json();
            return (data.products || []).map((product) => ({
              handle: product.handle,
              id: String(product.id),
            }));
          } catch (e) {
            return [];
          }
        }),
      );

      // Take the first product of each list, then the second, and so on.
      // Thus each seed adds products. Do not show seeds or cart products.
      const excluded = new Set([...cartHandles, this.currentHandle]);
      const picked = [];
      const seen = new Set();
      for (let i = 0; picked.length < this.maxProducts; i++) {
        if (lists.every((list) => i >= list.length)) break;
        for (const list of lists) {
          const product = list[i];
          if (!product || seen.has(product.handle)) continue;
          seen.add(product.handle);
          if (excluded.has(product.handle) || seeds.includes(product.id)) continue;
          picked.push({ handle: product.handle, variant: null });
          if (picked.length >= this.maxProducts) break;
        }
      }
      if (picked.length === 0) return;

      this.render(await this.fetchCards(picked, { prune: false }));
    }

    /**
     * @returns {Promise<{id: ?String, notFound: Boolean}>} notFound is true
     *   only for a 404 response, so a network error does not remove an entry.
     */
    async fetchProductId(handle) {
      const urlRoot = (this.dataset.urlRoot || "/").replace(/\/$/, "");
      try {
        const response = await fetch(
          `${urlRoot}/products/${encodeURIComponent(handle)}.js`,
        );
        if (!response.ok) return { id: null, notFound: response.status === 404 };
        return { id: String((await response.json()).id), notFound: false };
      } catch (e) {
        return { id: null, notFound: false };
      }
    }

    render(cards) {
      if (cards.length === 0) return;

      const perRowDesktop = parseInt(this.dataset.perRowDesktop, 10) || 4;
      const perRowTablet = parseInt(this.dataset.perRowTablet, 10) || 3;
      const perRowMobile = parseInt(this.dataset.perRowMobile, 10) || 2;
      const minPerRow = Math.min(perRowDesktop, perRowTablet, perRowMobile);

      const content = this.querySelector("[data-rv-content]");
      if (!content) return;

      if (cards.length > minPerRow) {
        const template = this.querySelector("[data-rv-carousel-template]");
        const carousel = template.content.cloneNode(true);
        const wrapper = carousel.querySelector(".swiper-wrapper");
        cards.forEach((card) => wrapper.appendChild(card));
        content.appendChild(carousel);
      } else {
        const template = this.querySelector("[data-rv-grid-template]");
        const listing = template.content.cloneNode(true);
        const grid = listing.querySelector(".product-grid");
        if (cards.length < perRowDesktop) {
          grid.classList.add("product-grid--centered-latori");
        }
        // In the grid the product blocks are direct grid children, without the
        // .swiper-slide wrapper (matching product-template-product-list.liquid).
        cards.forEach((card) => {
          while (card.firstElementChild) grid.appendChild(card.firstElementChild);
        });
        content.appendChild(listing);
      }

      this.querySelector("[data-rv-shell]").removeAttribute("hidden");
    }

    /**
     * @param {Object} [options]
     * @param {Boolean} [options.prune] - Remove handles with a 404 response
     *   from the storage (default true). Recommendations use false, because
     *   the storage does not contain them.
     */
    async fetchCards(entries, { prune = true } = {}) {
      const urlRoot = (this.dataset.urlRoot || "/").replace(/\/$/, "");
      const staleHandles = [];

      const results = await Promise.all(
        entries.map(async (entry) => {
          try {
            let url = `${urlRoot}/products/${encodeURIComponent(entry.handle)}?section_id=recently-viewed-card-latori`;
            if (entry.variant) {
              url += `&variant=${encodeURIComponent(entry.variant)}`;
            }
            const response = await fetch(url);
            if (!response.ok) {
              if (response.status === 404) staleHandles.push(entry.handle);
              return null;
            }
            const doc = new DOMParser().parseFromString(
              await response.text(),
              "text/html",
            );
            return doc.querySelector("[data-rv-card]");
          } catch (e) {
            return null;
          }
        }),
      );

      if (prune && staleHandles.length > 0) {
        this.writeStorage(
          this.readStorage().filter(
            (entry) => !staleHandles.includes(entry.handle),
          ),
        );
      }

      return results.filter(Boolean);
    }
  }

  customElements.define("recently-viewed-latori", RecentlyViewedLatori);
}
